import { signal } from "@preact/signals";

import * as analytics from "analytics";

import * as voiceStore from "./voiceStore";

import type {
  AbortRef,
  PlaybackHandle,
  SpeakOptions,
  TTSProvider,
  TTSVoice,
} from "./internalTypes";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserModelLike = any;

/** Picks a provider without knowing which concrete providers are installed. */
const providerFor = (
  providers: TTSProvider[],
  user: UserModelLike,
  voice: TTSVoice | undefined,
  feature: SpeakOptions["feature"],
  language: string
): TTSProvider => {
  if (voice) {
    const voiceProvider = providers.find(
      (provider) => provider.id === voice.provider
    );
    if (voiceProvider) return voiceProvider;
  }
  return (
    providers.find((provider) =>
      voiceStore.canUseProvider(user, provider, feature, language)
    ) ?? providers[providers.length - 1]
  );
};

export const _functionsForUnitTests = { providerFor };

/**
 * Hook installed by the app shell (app.ts) so tts can prompt the user to pick
 * an Auto-Pronounce voice without importing the picker view (which would create
 * a dependency cycle: picker → tts → picker). Bundles that don't install it,
 * such as the Web Reader, never prompt and play the default voice instead.
 * The hook is fired-and-forgotten; play() returns a no-op handle so the caller
 * doesn't proceed to synthesize.
 */
type NeedSpeakingModeVoiceHook = (language: string) => void;
let needSpeakingModeVoiceHook: NeedSpeakingModeVoiceHook | undefined;

export const setNeedSpeakingModeVoiceHook = (
  hook: NeedSpeakingModeVoiceHook | undefined
): void => {
  needSpeakingModeVoiceHook = hook;
};

/** Prompts for an Auto-Pronounce voice, if this bundle installed a picker */
export const promptForSpeakingModeVoice = (language: string): void => {
  needSpeakingModeVoiceHook?.(language);
};

const noopHandle: PlaybackHandle = { stop: () => {} };

// Tracks the currently active speak invocation so a subsequent play() can
// abort the previous one — including while it's still in the fetch/delay
// phase before its PlaybackHandle has been returned.
let currentAbort: AbortRef | undefined;
let playbackSequence = 0;

export const activeFeature = signal<SpeakOptions["feature"] | undefined>(
  undefined
);

/**
 * Single entry point for TTS playback. Resolves the user's saved voice for
 * the (feature, language), routes to a provider, and stops old playback.
 */
export const play = async (
  opts: SpeakOptions,
  providers: TTSProvider[],
  user: UserModelLike
): Promise<PlaybackHandle> => {
  cancel();

  const abort: AbortRef = { aborted: false };
  currentAbort = abort;
  const playbackId = ++playbackSequence;
  const clearActiveFeature = () => {
    if (playbackId === playbackSequence) {
      activeFeature.value = undefined;
    }
  };

  const resolvedVoice =
    opts.voice ??
    voiceStore.resolveVoice(user, opts.feature, opts.language, providers);

  const resolvedRate =
    opts.rate ??
    voiceStore.getRate(user, opts.feature, opts.language, providers);

  const tier = voiceStore.tierFromUser(user);

  // Free users with no saved Auto-Pronounce voice should choose explicitly
  // instead of silently falling back to an arbitrary device default.
  if (
    !resolvedVoice &&
    !opts.voice &&
    !opts.preview &&
    opts.feature === "speakingMode" &&
    tier === "free" &&
    needSpeakingModeVoiceHook
  ) {
    needSpeakingModeVoiceHook(opts.language);
    // Callers (RemoteWordListView, FlashcardsPageView, ...) set a loading
    // state before calling play() and rely on onStart/onEnd/onError to
    // clear it. The picker-prompt path doesn't actually start playback,
    // so fire onEnd here — otherwise the speaker spinner sticks
    // indefinitely while the picker is open.
    opts.onEnd?.();
    return noopHandle;
  }

  const provider = providerFor(
    providers,
    user,
    resolvedVoice,
    opts.feature,
    opts.language
  );

  if (opts.feature === "readAloud") {
    // Unified Read Aloud tracking. ttsProvider lets us slice usage by
    // engine (browser vs Azure) without needing two separate events.
    const text =
      typeof opts.text === "string"
        ? opts.text
        : opts.text.map((part) => part.text).join("");
    analytics.trackEventWithProperties("readAloud", {
      ttsProvider: provider.id,
      voice: resolvedVoice?.id,
      characters: text.length,
      language: opts.language,
    });
  }

  const handle = await provider.speak({
    ...opts,
    voice: resolvedVoice,
    rate: resolvedRate,
    onStart: () => {
      if (playbackId === playbackSequence) {
        activeFeature.value = opts.feature;
      }
      opts.onStart?.();
    },
    onEnd: (nextPosition) => {
      clearActiveFeature();
      opts.onEnd?.(nextPosition);
    },
    onError: (err) => {
      clearActiveFeature();
      opts.onError?.(err);
    },
    abort,
  });

  // If we were aborted between speak() starting and resolving, tear down the
  // handle now so it doesn't trample module-level provider state.
  if (abort.aborted) handle.stop();

  return handle;
};

/**
 * Stops any in-progress TTS playback. Safe to call when nothing is playing.
 */
export const cancel = (): void => {
  if (!currentAbort) return;
  activeFeature.value = undefined;
  currentAbort.aborted = true;
  currentAbort.stop?.();
  currentAbort = undefined;
};
